import axios from 'axios';
import { getToken, getCurrentAuthContext } from './auth';
import { devLog, devError, IS_DEV } from '../utils/devLog';
import {
  attachApproxRequestPayloadBytes,
  logAxiosErrorDiagnostics,
} from '../utils/apiDiagnostics';

// Default relativo funciona em produção (Nginx /api) e em dev via proxy do CRA.
const API_URL = process.env.REACT_APP_API_URL || '/api';
const VERBOSE_API_ERRORS = process.env.REACT_APP_VERBOSE_API_ERRORS === 'true';

/** URLs ngrok gratuitas podem devolver HTML de aviso sem headers CORS; o browser acusa falha de CORS. */
const isNgrokApiBase = /ngrok(-free)?\.app|\.ngrok\.io|\.ngrok\.app/i.test(API_URL);
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    ...(isNgrokApiBase ? { 'ngrok-skip-browser-warning': 'true' } : {}),
  },
});
// Interceptor para adicionar o token de autenticação em todas as requisições
api.interceptors.request.use(
  (config) => {
    const context = getCurrentAuthContext();
    const token = getToken(context);

    devLog('🔍 [API INTERCEPTOR] URL:', config.url);
    devLog('🔍 [API INTERCEPTOR] Contexto:', context);
    devLog('🔍 [API INTERCEPTOR] Token:', token ? `${token.substring(0, 20)}...` : 'null');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      devLog('✅ [API INTERCEPTOR] Authorization anexado');
    } else {
      devLog('❌ [API INTERCEPTOR] Sem token para o contexto:', context);
    }

    attachApproxRequestPayloadBytes(config);

    return config;
  },
  (error) => {
    devError('❌ [API INTERCEPTOR] Erro na requisição:', error);
    return Promise.reject(error);
  }
);

// Interceptor para tratamento global de erros
api.interceptors.response.use(
  (response) => {
    devLog('✅ [API RESPONSE]', response.config?.url, response.status);
    return response;
  },
  (error) => {
    if (error.response) {
      const st = error.response.status;
      if (VERBOSE_API_ERRORS || st === 413 || st === 502) {
        logAxiosErrorDiagnostics('Axios interceptor (falha HTTP)', error);
      }
      devError(
        '[API]',
        error.config?.url,
        error.response?.status,
        error.response?.data?.message || error.response?.data
      );
      if (IS_DEV && error.response.status === 401) {
        devError('[API] 401 — sessão expirada ou inválida');
      }
    } else if (error.request) {
      devError('[API] Sem resposta do servidor:', error.config?.url);
    } else {
      devError('[API] Erro na requisição:', error.message);
    }

    return Promise.reject(error);
  }
);

export default api;

// Função auxiliar para obter o token de autenticação
export function getAuthHeader() {
  const context = getCurrentAuthContext();
  const token = getToken(context);
  return token ? { Authorization: `Bearer ${token}` } : {};
}
